import {
  DatePicker,
  Field,
  Input,
  RadioGroup,
  Select,
  Spin,
  TextArea,
} from '@/components/commons';
import { FormField } from '@/components/commons/FormField';
import { ArticleService, OfferService } from '@/services';
import { ENDPOINT } from '@/services/endpoint';
import {
  FALLBACK_URL,
  FILE_SIZE_LIMIT,
  SUPPORTED_EXTENSION,
  SUPPORTED_FORMATS,
} from '@/services/image';
import {
  ChatFukumaruSourceType,
  chatFukumaruSourceTypeJP,
  IGetDetailChatFukumaruResponse,
} from '@/types/response/chatFukumaru';
import { Status, notificationStatus } from '@/utils';
import { CloseOutlined } from '@ant-design/icons';
import { Badge, Button, Image, Modal, Radio, Upload } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import moment, { Moment } from 'moment';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useQuery } from 'react-query';
interface IProps {
  isLoading: boolean;
  openDrawer: boolean;
  chatFukumaru?: IGetDetailChatFukumaruResponse;
}

export const ChatFukumaruForm: React.FC<IProps> = ({
  isLoading,
  openDrawer,
  chatFukumaru,
}) => {
  const { trigger, watch, setValue, setError, clearErrors } = useFormContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSourceId, setSelectedSourceId] = useState<number | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState('');

  const sourceType = watch('sourceType') as ChatFukumaruSourceType | undefined;

  const sourceId = watch('sourceId');

  const status = watch('status');

  const publicAt = watch('publicAt');

  const { data: articlesData, isFetching: isFetchingArticles } = useQuery(
    [ENDPOINT.ARTICLE.GET_CHECKBOX_LIST, { chatFukumaruId: chatFukumaru?.id }],
    () => ArticleService.getCheckboxList({ chatFukumaruId: chatFukumaru?.id as number }),
    {
      enabled:
        (isModalOpen && sourceType === ChatFukumaruSourceType.ARTICLE) ||
        (sourceType === ChatFukumaruSourceType.ARTICLE && !!sourceId && openDrawer),
    },
  );

  const { data: offerData, isFetching: isFetchingOffers } = useQuery(
    [ENDPOINT.OFFER.GET_CHECKBOX_LIST, { chatFukumaruId: chatFukumaru?.id }],
    () => OfferService.getCheckboxList({ chatFukumaruId: chatFukumaru?.id as number }),
    {
      enabled:
        (isModalOpen && sourceType === ChatFukumaruSourceType.OFFER) ||
        (sourceType === ChatFukumaruSourceType.OFFER && !!sourceId && openDrawer),
    },
  );

  useEffect(() => {
    if (openDrawer) {
      setIsInitialLoad(true);
      const currentSourceId = watch('sourceId');
      setSelectedSourceId(currentSourceId || null);
      const timer = setTimeout(() => {
        setIsInitialLoad(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [openDrawer]);

  const sourceTypeOptions = [
    {
      value: ChatFukumaruSourceType.ARTICLE,
      label: chatFukumaruSourceTypeJP[ChatFukumaruSourceType.ARTICLE],
    },
    {
      value: ChatFukumaruSourceType.OFFER,
      label: chatFukumaruSourceTypeJP[ChatFukumaruSourceType.OFFER],
    },
  ];

  const getModalData = () => {
    if (sourceType === ChatFukumaruSourceType.ARTICLE) {
      return articlesData?.data || [];
    }
    if (sourceType === ChatFukumaruSourceType.OFFER) {
      return offerData?.data || [];
    }

    return [];
  };

  const modalOptions = getModalData()
    .map((item: any) => ({
      ...item,
      value: Number(item?.id ?? item?.value),
      label: String(item?.name ?? item?.title ?? item?.label ?? ''),
      image:
        item?.imageCover?.url ??
        item?.image?.url ??
        item?.coverImageUrl ??
        item?.imageUrl ??
        null,
    }))
    .filter((option) => {
      if (!searchKeyword.trim()) return true;
      return option.label.toLowerCase().includes(searchKeyword.toLowerCase());
    });

  const handleModalOk = () => {
    if (selectedSourceId !== null) {
      // Kiểm tra xem selectedSourceId có tồn tại trong danh sách filtered không
      const isSelectedInFilteredList = modalOptions.some(
        (option) => option.value === selectedSourceId,
      );

      if (isSelectedInFilteredList) {
        setValue('sourceId', selectedSourceId, { shouldValidate: true });
        setIsModalOpen(false);
        setSearchKeyword('');
      }
    }
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setSearchKeyword('');
    const currentSourceId = watch('sourceId');
    setSelectedSourceId(currentSourceId || null);
  };

  const handleSourceTypeChange = (
    value: ChatFukumaruSourceType | null | undefined,
  ) => {
    if (!value) {
      setValue('sourceType', undefined);
      setValue('sourceId', undefined);
      setSelectedSourceId(null);
      trigger('sourceId');
      return;
    }
    setValue('sourceType', value, { shouldValidate: true });
    setValue('sourceId', undefined);
    setSelectedSourceId(null);
    trigger('sourceId');
  };

  const disabledDate = (current: Dayjs) => {
    return current.isBefore(dayjs().startOf('day'));
  };

  const disabledTime = (current: Dayjs) => {
    if (!current) return {};

    const now = dayjs();
    const selectedDate = dayjs(current);

    if (selectedDate.isBefore(now, 'day')) {
      return {
        disabledHours: () => Array.from({ length: 24 }, (_, i) => i),
        disabledMinutes: () => Array.from({ length: 60 }, (_, i) => i),
      };
    }

    if (selectedDate.isSame(now, 'day')) {
      const disabledHours: number[] = [];
      for (let i = 0; i < now.hour(); i++) {
        disabledHours.push(i);
      }

      const disabledMinutes = (selectedHour: number) => {
        if (selectedHour === now.hour()) {
          const minutes = [];
          for (let i = 0; i <= now.minute(); i++) {
            minutes.push(i);
          }
          return minutes;
        }
        return [];
      };

      return {
        disabledHours: () => disabledHours,
        disabledMinutes: disabledMinutes,
      };
    }

    return {
      disabledHours: () => [],
      disabledMinutes: () => [],
    };
  };


  const image = watch('image');
  const attachments = watch('attachments');

  const handleUpload = (info: any) => {
    const file = info.file.originFileObj || info.file;
    const reader = new FileReader();
    reader.onload = () => {
      setValue('image', reader.result as string);
      setValue('attachments', info);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setValue('image', null);
    setValue('attachments', null);
  };

  const beforeUpload = (file: File) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (file.size > FILE_SIZE_LIMIT) {
      setError('attachment', {
        type: 'validate',
        message: '画像のサイズは10MBを超えています。',
      });
      return Upload.LIST_IGNORE;
    }

    if (
      !SUPPORTED_EXTENSION.includes(fileExtension || '') ||
      !SUPPORTED_FORMATS.includes(file.type)
    ) {
      setError('attachment', {
        type: 'validate',
        message: '画像は .jpg、.png、.jpeg 形式ではありません。',
      });
      return Upload.LIST_IGNORE;
    }
    clearErrors('attachment');

    return true;
  };

  const isDisabled = watch('isDisabled');

  return (
    <>
      <Spin spinning={isLoading}>
        <Field label='タイトル' name='title' required>
          <Input
            placeholder='入力してください'
            maxLength={100}
            disabled={isDisabled}
          />
        </Field>
        <Field label='本文' name='message' required>
          <TextArea
            placeholder='入力してください'
            rows={4}
            maxLength={10000}
            disabled={isDisabled}
          />
        </Field>
        <Field label='URL' name='url'>
          <Input
            placeholder='URLを入力してください'
            disabled={isDisabled}
          />
        </Field>
        {!attachments?.fileList?.length && !image && (
          <Field
            label='メッセージ下の表示画像'
            name='attachments'
            className='h-[80px]'
          >
            <div className='border-solid rounded border-2 border-zinc-300  w-[200px] h-[100px] flex justify-center items-center'>
              <Upload
                customRequest={() => {}}
                listType='text'
                maxCount={1}
                beforeUpload={beforeUpload}
                showUploadList={false}
                onChange={handleUpload}
                disabled={isDisabled}
              >
                <Button
                  type='default'
                  shape='default'
                  size='middle'
                  className='text-[#84ab42] border border-[#84ab42] font-semibold'
                  disabled={isDisabled}
                >
                  画像を選択
                </Button>
              </Upload>
            </div>
          </Field>
        )}
        {image && openDrawer && !isLoading && (
          <Field
            label='メッセージ下の表示画像'
            name='image'
            className='h-[80px]'
          >
            <div className='relative inline-block'>
              <Image
                height={100}
                src={image}
                className='block rounded-lg'
                fallback={FALLBACK_URL}
              />
              {!isDisabled && (
                <Button
                  type='primary'
                  shape='circle'
                  icon={<CloseOutlined />}
                  size='small'
                  onClick={handleRemoveImage}
                  className='absolute -top-2 -right-2 z-10 bg-red-500 border-none'
                />
              )}
            </div>
          </Field>
        )}
        <Field name='attachment'>
          <div></div>
        </Field>
        <Field label='ソースタイプ' name='sourceType'>
          <Select
            placeholder='ソースタイプを選択'
            style={{ width: '100%' }}
            options={sourceTypeOptions}
            onChange={handleSourceTypeChange}
            allowClear
            disabled={isDisabled}
          />
        </Field>
        <Field label='選択済みソース' name='sourceId' required={!!sourceType}>
          <div className='flex gap-2'>
            <Input
              placeholder='ソースを選択してください'
              value={
                modalOptions.find((opt) => opt.value === watch('sourceId'))
                  ?.label || ''
              }
              readOnly
              className='flex-1'
              disabled={isDisabled}
            />
            <Button
              type='default'
              onClick={() => {
                const currentSourceId = watch('sourceId');
                setSelectedSourceId(currentSourceId || null);
                setIsModalOpen(true);
              }}
              disabled={isDisabled || !sourceType}
            >
              選択
            </Button>
          </div>
        </Field>
        <label className='title-calendar font-semibold'>
          発信日時
          <Badge status='error' className='ml-2' />
        </label>
        <div className='flex items-baseline	gap-1 pt-2'>
          <Field name='startAt' className='flex-1'>
            <DatePicker
              disabledDate={disabledDate}
              disabledTime={disabledTime}
              showTime
              onCalendarChange={(e) => {
                setValue('startAt', e ? (e as Moment).second(0) : undefined);
                trigger('startAt');
              }}
              disabled={isDisabled}
            />
          </Field>
        </div>
        <FormField label='配信ステータス' name='status' className='w-full'>
          <RadioGroup options={notificationStatus} disabled={isDisabled}/>
        </FormField>
      </Spin>
      <Modal
        title='ソースを選択'
        open={isModalOpen}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText='確定'
        cancelText='キャンセル'
        okButtonProps={{
          disabled:
            selectedSourceId === null ||
            !modalOptions.some((option) => option.value === selectedSourceId),
        }}
        width={600}
        loading={isFetchingArticles || isFetchingOffers}
      >
        <div style={{ marginBottom: '16px' }}>
          <Input
            placeholder='キーワードで検索'
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            allowClear
            style={{ width: '100%' }}
          />
        </div>
        <Radio.Group
          value={selectedSourceId}
          onChange={(e) => setSelectedSourceId(e.target.value)}
          style={{ width: '100%' }}
        >
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {modalOptions.length === 0 ? (
              <div
                style={{ textAlign: 'center', padding: '20px', color: '#999' }}
              >
                検索結果が見つかりません
              </div>
            ) : (
              modalOptions.map((option) => (
                <Radio
                  key={option.value}
                  value={option.value}
                  style={{
                    display: 'flex',
                    gap: '12px',
                    marginBottom: '12px',
                    padding: '8px',
                  }}
                  disabled={
                    option.publicEndAt &&
                    dayjs(option.publicEndAt).isBefore(dayjs())
                  }
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <div className='flex-1'>
                      {option.image && (
                        <Image
                          src={option.image}
                          alt={option.label}
                          width={120}
                          height={120}
                          style={{ objectFit: 'cover', borderRadius: '4px' }}
                          fallback={FALLBACK_URL}
                        />
                      )}
                    </div>
                    <span>{option.label}</span>
                  </div>
                </Radio>
              ))
            )}
          </div>
        </Radio.Group>
      </Modal>
    </>
  );
};

